import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./store";
import { useLazyGetAllBanksQuery } from "../services/banks";
import {
  setBanks,
  addBank,
  updateBank,
  removeBank,
  selectBanksList,
  selectIsBanksCacheValid,
  BankListItem,
} from "../reducers/banksSlice";
import { getErrorMessage } from "../types/api";

interface UseBanksOptions {
  fetchOnMount?: boolean;
}

interface UseBanksReturn {
  banks: BankListItem[];
  isLoading: boolean;
  error: string | null;
  isCacheValid: boolean;
  fetchBanks: () => Promise<void>;
  refetch: () => Promise<void>;
  addBankToStore: (bank: BankListItem) => void;
  updateBankInStore: (id: string, data: Partial<BankListItem>) => void;
  removeBankFromStore: (id: string) => void;
}

const useBanks = (options: UseBanksOptions = {}): UseBanksReturn => {
  const { fetchOnMount = true } = options;

  const dispatch = useAppDispatch();
  const banks = useAppSelector(selectBanksList);
  const isCacheValid = useAppSelector(selectIsBanksCacheValid);

  const [getAllBanksApi] = useLazyGetAllBanksQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFromApi = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllBanksApi().unwrap();
      if (response?.statusCode === 200) {
        const allData =
          response?.data?.data?.map((data: { _id: string }) => ({
            ...data,
            id: data?._id,
          })) || [];
        dispatch(
          setBanks({
            list: allData,
            lastFetchedAt: Date.now(),
          })
        );
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load banks"));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, getAllBanksApi]);

  const fetchBanks = useCallback(async () => {
    if (isCacheValid && banks.length > 0) {
      return;
    }
    await fetchFromApi();
  }, [isCacheValid, banks.length, fetchFromApi]);

  const refetch = useCallback(async () => {
    await fetchFromApi();
  }, [fetchFromApi]);

  const addBankToStore = useCallback(
    (bank: BankListItem) => {
      dispatch(addBank(bank));
    },
    [dispatch]
  );

  const updateBankInStore = useCallback(
    (id: string, data: Partial<BankListItem>) => {
      dispatch(updateBank({ id, data }));
    },
    [dispatch]
  );

  const removeBankFromStore = useCallback(
    (id: string) => {
      dispatch(removeBank(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (fetchOnMount) {
      fetchBanks();
    }
  }, []);

  return {
    banks,
    isLoading,
    error,
    isCacheValid,
    fetchBanks,
    refetch,
    addBankToStore,
    updateBankInStore,
    removeBankFromStore,
  };
};

export default useBanks;
